import React, { useEffect, useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Video, Link2, BarChart3, CheckCircle, XCircle, Loader2, Download, Eye, Facebook, Instagram, FileText, Copy, RotateCcw } from 'lucide-react';

const API_BASE = import.meta.env.VITE_CMS_API_URL || 'https://marveland-cms.jimsbond007.workers.dev';

// ── 影片版面規格（提示詞）──
// 這是把用戶歷輪口語指令（v4→v9 迭代）吸收整理成的「版面密碼」：改這段文字＝改規格，
// 修改後複製貼給 AI 即可按新規格調整影片模板。存放 localStorage，不會上傳。
const DEFAULT_VIDEO_SPEC = `【Marveland 物業影片版面規格 v9（定案）】

■ 全片
- 尺寸：1080×1080 方形（可選 1080×1920 直式）
- 時長：每張照片 5 秒；Agent 頁最後 5 秒，定格到結尾（不淡出）
- 背景：深藍 #0B1220；品牌色：金 #D4AF37、白 #FFFFFF
- LOGO：全片常駐右上角，距頂 36px、距右 20px，縮放 8%
- 字體：Noto Sans TC 700／900（自架字體）
- 背景音樂：4 風格可選（現代簡約／奢華時尚／工業節奏／溫馨舒適）

■ 照片頁（每張 5 秒輪播）
- 照片全幅 Ken Burns（奇數張 zoomIn、偶數張 zoomOut），淡入淡出轉場
- 第 1 張三層文字（由上至下堆疊、間距 13-20px、互不重疊）：
  · 金色眉標「MARVELAND 精選物業」（30px，無背景）
  · 標題盒：深藍 85%、圓角 24、白字 64px/900
  · 完整內文盒：深藍 80%、圓角 20、白字 36px、寬 920，文案完整保留不刪減
- 第 2 張：價格盒（金字 80px/900）＋「面積｜地址」盒（白字 44px）
- 第 3 張起：金色 CTA「專人預約睇樓」（深藍字 56px/900、圓角 28）

■ Agent 頁（最後 5 秒）
- 白底全幅；Agent 相片佔畫面大部分（86%，方形，上移 2%）
- 右下單一深藍半透明容器（78% 透明度、圓角 18、寬 460×高 130、距底 110px、距右 43px）：
  · 左側嵌 QR Code（115px、垂直居中、上下各留約 7px padding）
  · 姓名（白字 42px/900）與電話（金字 30px/700）靠右對齊（距容器右緣 20px）
  · 電話自動去除 (852) 國碼前綴
- QR 連結：物業詳情頁（附 utm_source 追蹤）

■ 照片水印（媒體庫上傳時自動套用）
- 中央對角 MARVELAND：灰色 #8A8A8A、寬度＝對角線 55%、-45° 沿對角線
- 防移除：overlay 混合咬進紋理＋重影雙層＋JPEG 破壞性輸出（修圖即損圖）`;

const SPEC_STORAGE_KEY = 'marveland_video_spec_v1';

interface PropertyData {
  id: string;
  title: string;
  price: string;
  area: string;
  address: string;
  description: string;
  images: string[];
  agentName: string;
  agentPhone: string;
  agentWhatsapp: string;
  agentPhoto?: string;
}

const mockProperty: PropertyData = {
  id: 'p001',
  title: '荃灣海景甲級寫字樓',
  price: '售價：$880萬',
  area: '面積：約 1,250 呎',
  address: '荃灣楊屋道1號',
  description: '高層海景單位，坐擁全海景觀，光猛開揚。特高樓底，間隔四正，企理裝修，即租即用。鄰近荃灣西港鐵站，交通方便。設有獨立洗手間及茶水間，冷氣齊全，管理完善。適合各類型公司進駐。歡迎預約睇樓。',
  images: [
    'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800',
    'https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=800',
    'https://images.unsplash.com/photo-1524758631624-e2822e304c36?w=800',
  ],
  agentName: 'Peggy Leung',
  agentPhone: '(852) 9236 8966',
  agentWhatsapp: 'https://wa.me/85292368966',
  agentPhoto: 'https://marveland-cms.jimsbond007.workers.dev/api/media/media_1787232719835_64ymrp',
};

const Staging: React.FC = () => {
  const [testMode, setTestMode] = useState(true);
  // 影片版面規格（提示詞）：可編輯、存 localStorage；修改後複製貼給 AI 溝通
  const [videoSpec, setVideoSpec] = useState(DEFAULT_VIDEO_SPEC);
  const [specCopied, setSpecCopied] = useState(false);
  useEffect(() => {
    const saved = localStorage.getItem(SPEC_STORAGE_KEY);
    if (saved) setVideoSpec(saved);
  }, []);
  const onSpecChange = (v: string) => {
    setVideoSpec(v);
    localStorage.setItem(SPEC_STORAGE_KEY, v);
  };
  const copySpec = async () => {
    try {
      await navigator.clipboard.writeText(videoSpec);
      setSpecCopied(true);
      setTimeout(() => setSpecCopied(false), 2000);
    } catch {
      window.prompt('複製以下規格：', videoSpec);
    }
  };
  const [syncToFacebook, setSyncToFacebook] = useState(true);
  const [syncToInstagram, setSyncToInstagram] = useState(false);
  const [publishMode, setPublishMode] = useState<'photo' | 'video' | 'both'>('photo');
  const [videoStyle, setVideoStyle] = useState('modern');
  const [bgm, setBgm] = useState('modern');
  const [includeAgentPhoto, setIncludeAgentPhoto] = useState(true);
  const [customHashtags, setCustomHashtags] = useState('#荀盤 #新淨 #即租即用');
  const [customMessage, setCustomMessage] = useState('');
  const [aspectRatio, setAspectRatio] = useState<'square' | 'portrait'>('square');
  const [videoEngine, setVideoEngine] = useState<'shotstack' | 'ice' | 'zhipu' | 'aipipe'>('shotstack');
  // Agent 照片 URL（mockup 測試用：目前系統內代理尚未上傳照片，可貼任意 URL 測試影片 Agent 頁）
  const [agentPhotoUrl, setAgentPhotoUrl] = useState(mockProperty.agentPhoto || '');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    message: string;
    videoUrl?: string;
    postId?: string;
    shortLink?: string;
    status?: 'generating' | 'pendingApproval' | 'done';
    platformResults?: { platform: string; success: boolean; message: string; postId?: string }[];
  } | null>(null);
  const [quota, setQuota] = useState({ used: 5, total: 50 });

  // 文案從物業內容完整抽取（標題＋價格面積＋地址＋內文細節＋負責代理＋WhatsApp），不做事前刪減，客人可在自訂文案框自行刪減
  const defaultMessage = () =>
    `【新盤速遞】${mockProperty.title}\n${mockProperty.price} | ${mockProperty.area}\n地址：${mockProperty.address}\n\n${mockProperty.description}\n\n負責代理：${mockProperty.agentName}\nWhatsApp 查詢：${mockProperty.agentWhatsapp}`;

  const generateMessage = () => {
    const defaultHashtags = `#Marveland #金晉物業 #${mockProperty.title.split('').slice(0, 2).join('')}寫字樓`;
    const message = customMessage || defaultMessage();
    return `${message}\n\n${defaultHashtags} ${customHashtags}`;
  };

  const withLink = (message: string, link: string) =>
    link ? `${message}\n\n立即了解：${link}` : message;

  const handleSubmit = async () => {
    setLoading(true);
    setResult(null);

    try {
      if (!syncToFacebook && !syncToInstagram) {
        setResult({ success: true, message: '物業已儲存（未同步到任何社群平台）', status: 'done' });
        setLoading(false);
        return;
      }

      let videoUrl = '';
      let shortLink = '';

      // 生成短網址（照片與影片模式都會用到）
      const shortLinkRes = await fetch(`${API_BASE}/api/staging/shortlink-test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: `https://marveland.hk/properties/${mockProperty.id}?utm_source=facebook&utm_medium=${publishMode}&utm_campaign=${mockProperty.id}`,
        }),
      });
      const shortLinkData = await shortLinkRes.json();
      if (shortLinkData.success) {
        shortLink = shortLinkData.shortLink;
      }

      // 如果選擇生成影片（只發佈影片或同時發佈）
      if (publishMode === 'video' || publishMode === 'both') {
        // 檢查 Quota
        if (quota.used >= quota.total) {
          setResult({ success: false, message: '本月 Quota 已用完，請聯絡我們購買更多 Quota' });
          setLoading(false);
          return;
        }

        // 生成影片（引擎可選：Shotstack／ICE 模板剪輯；智譜純 AI；AI 管線＝AI 素材＋品牌合成）
        const engineEndpoints: Record<string, { submit: string; status: string }> = {
          shotstack: { submit: 'generate-video', status: 'video-status' },
          ice: { submit: 'ice-generate-video', status: 'ice-video-status' },
          zhipu: { submit: 'zhipu-generate-video', status: 'zhipu-video-status' },
          aipipe: { submit: 'aipipe-generate-video', status: 'aipipe-video-status' },
        };
        const endpoints = engineEndpoints[videoEngine] || engineEndpoints.shotstack;
        const engineMessages: Record<string, string> = {
          shotstack: '影片生成中，請稍候...',
          ice: '已提交阿里雲 ICE，合成中，請稍候...',
          zhipu: '已提交智譜 AI 生成（約 1-5 分鐘），請稍候...',
          aipipe: 'AI 管線啟動：實景素材生成中（1-5 分鐘），完成後自動品牌合成...',
        };
        setResult({ success: true, message: engineMessages[videoEngine] || engineMessages.shotstack, status: 'generating' });
        const videoRes = await fetch(`${API_BASE}/api/staging/${endpoints.submit}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            propertyId: mockProperty.id,
            images: mockProperty.images,
            title: mockProperty.title,
            price: mockProperty.price,
            area: mockProperty.area,
            address: mockProperty.address,
            description: mockProperty.description,
            agentName: mockProperty.agentName,
            agentPhone: mockProperty.agentPhone,
            agentPhoto: includeAgentPhoto ? (agentPhotoUrl.trim() || undefined) : undefined,
            bgmStyle: bgm,
            qrUrl: shortLink || undefined,
            aspectRatio,
          }),
        });
        const videoData = await videoRes.json();

        if (!videoData.success) {
          setResult({ success: false, message: videoData.message });
          setLoading(false);
          return;
        }

        // 輪詢查詢影片狀態，直到完成或失敗
        // AI 生成需 1-5 分鐘；AI 管線（素材＋合成）最長約 7 分鐘
        let attempts = 0;
        const maxAttempts = videoEngine === 'aipipe' ? 210 : videoEngine === 'zhipu' ? 150 : 30;
        while (attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          const statusRes = await fetch(`${API_BASE}/api/staging/${endpoints.status}/${videoData.renderId}`);
          const statusData = await statusRes.json();

          if (statusData.success && statusData.videoUrl) {
            videoUrl = statusData.videoUrl;
            break;
          }

          if (statusData.status === 'failed') {
            setResult({ success: false, message: statusData.message || '影片生成失敗，請重試' });
            setLoading(false);
            return;
          }

          attempts++;
        }

        if (!videoUrl) {
          setResult({ success: false, message: '影片生成超時，請稍後重試' });
          setLoading(false);
          return;
        }

        // 更新 Quota
        setQuota({ ...quota, used: quota.used + 1 });

        // 顯示影片預覽，讓客人審核
        setResult({
          success: true,
          message: '影片已生成，請審核後確認發佈',
          videoUrl,
          shortLink,
          status: 'pendingApproval',
        });
      } else {
        // 只發佈照片，直接發佈到已選平台
        const message = generateMessage();
        const platformResults: { platform: string; success: boolean; message: string; postId?: string }[] = [];

        if (syncToFacebook) {
          const fbRes = await fetch(`${API_BASE}/api/staging/fb-test-post`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              testMode,
              message: withLink(message, shortLink),
              imageUrls: mockProperty.images,
            }),
          });
          const fbData = await fbRes.json();
          platformResults.push({ platform: 'Facebook', success: fbData.success, message: fbData.message, postId: fbData.postId });
        }

        if (syncToInstagram) {
          const igRes = await fetch(`${API_BASE}/api/staging/ig-test-post`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              testMode,
              message: withLink(message, shortLink),
              imageUrls: mockProperty.images,
              aspectRatio,
            }),
          });
          const igData = await igRes.json();
          platformResults.push({ platform: 'Instagram', success: igData.success, message: igData.message, postId: igData.postId });
        }

        const allOk = platformResults.every(p => p.success);
        setResult({
          success: allOk,
          message: platformResults.map(p => `${p.platform}：${p.message}`).join('\n'),
          postId: platformResults.find(p => p.platform === 'Facebook')?.postId,
          platformResults,
          status: allOk ? 'done' : undefined,
        });
      }
    } catch (err) {
      setResult({ success: false, message: String(err) });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!result?.videoUrl) return;

    setLoading(true);
    try {
      const message = generateMessage();
      const platformResults: { platform: string; success: boolean; message: string; postId?: string }[] = [];

      if (syncToFacebook && publishMode === 'both') {
        // Facebook 策略：影片貼文（自然流量）＋照片貼文（付費廣告素材）
        const videoPostRes = await fetch(`${API_BASE}/api/staging/fb-test-post`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            testMode,
            message: withLink(message, result.shortLink || ''),
            videoUrl: result.videoUrl,
          }),
        });
        const videoPostData = await videoPostRes.json();
        platformResults.push({ platform: 'Facebook 影片貼文', success: videoPostData.success, message: videoPostData.message, postId: videoPostData.postId });

        const photoPostRes = await fetch(`${API_BASE}/api/staging/fb-test-post`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            testMode,
            message: withLink(message, result.shortLink || ''),
          }),
        });
        const photoPostData = await photoPostRes.json();
        platformResults.push({ platform: 'Facebook 照片貼文', success: photoPostData.success, message: photoPostData.message, postId: photoPostData.postId });
      } else if (syncToFacebook) {
        // 只發佈影片
        const fbRes = await fetch(`${API_BASE}/api/staging/fb-test-post`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            testMode,
            message: withLink(message, result.shortLink || ''),
            videoUrl: result.videoUrl,
          }),
        });
        const fbData = await fbRes.json();
        platformResults.push({ platform: 'Facebook', success: fbData.success, message: fbData.message, postId: fbData.postId });
      }

      if (syncToInstagram) {
        // IG 策略：影片發佈一支（9:16→Reels / 1:1→影片貼文），不重複發照片輪播
        const igRes = await fetch(`${API_BASE}/api/staging/ig-test-post`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            testMode,
            message: withLink(message, result.shortLink || ''),
            videoUrl: result.videoUrl,
            aspectRatio,
          }),
        });
        const igData = await igRes.json();
        platformResults.push({ platform: 'Instagram', success: igData.success, message: igData.message, postId: igData.postId });
      }

      const allOk = platformResults.length > 0 && platformResults.every(p => p.success);
      setResult({
        success: allOk,
        message: platformResults.map(p => `${p.platform}：${p.message}`).join('\n'),
        videoUrl: result.videoUrl,
        postId: platformResults[0]?.postId,
        shortLink: result.shortLink,
        platformResults,
        status: allOk ? 'done' : undefined,
      });
    } catch (err) {
      setResult({ success: false, message: String(err) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans text-slate-900">
      <Header />
      <main>
        <section className="relative bg-slate-900 py-24">
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 to-slate-800" />
          <div className="container mx-auto px-4 relative z-10">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Staging 測試環境</h1>
            <p className="text-slate-300 text-lg max-w-2xl">模擬現有物業編輯，測試 Facebook / Instagram 同步與影片生成功能</p>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4 max-w-4xl">
            {/* 測試模式警告 */}
            <div className="bg-yellow-100 border-l-4 border-yellow-500 p-4 mb-8">
              <p className="font-bold text-yellow-800">⚠️ 測試模式</p>
              <p className="text-sm text-yellow-700">這個頁面用於測試新功能，不影響正式網站。所有測試發佈到 Facebook 的貼文都是「僅自己可見」。</p>
            </div>

            {/* 模擬物業編輯表單 */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">物業資料（模擬）</h2>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">標題</label>
                  <input
                    type="text"
                    value={mockProperty.title}
                    disabled
                    className="w-full rounded border border-slate-300 px-3 py-2 text-sm bg-slate-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">售價</label>
                  <input
                    type="text"
                    value={mockProperty.price}
                    disabled
                    className="w-full rounded border border-slate-300 px-3 py-2 text-sm bg-slate-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">面積</label>
                  <input
                    type="text"
                    value={mockProperty.area}
                    disabled
                    className="w-full rounded border border-slate-300 px-3 py-2 text-sm bg-slate-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">地址</label>
                  <input
                    type="text"
                    value={mockProperty.address}
                    disabled
                    className="w-full rounded border border-slate-300 px-3 py-2 text-sm bg-slate-50"
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-semibold text-slate-700 mb-1">圖片</label>
                <div className="flex gap-2">
                  {mockProperty.images.map((img, i) => (
                    <img key={i} src={img} alt="" className="h-20 w-20 rounded object-cover" />
                  ))}
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-semibold text-slate-700 mb-1">
                  Agent 照片 URL（測試用——目前系統內代理尚未上傳照片，可貼上任意圖片 URL 測試影片 Agent 頁）
                </label>
                <div className="flex items-center gap-3">
                  {agentPhotoUrl ? (
                    <img
                      src={agentPhotoUrl}
                      alt="Agent 照片預覽"
                      className="h-14 w-14 shrink-0 rounded-full border-2 border-amber-500 object-cover bg-slate-100"
                      onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.3'; }}
                    />
                  ) : (
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border-2 border-dashed border-slate-300 bg-slate-50 text-xs text-slate-400">無</div>
                  )}
                  <input
                    type="url"
                    value={agentPhotoUrl}
                    onChange={(e) => setAgentPhotoUrl(e.target.value)}
                    placeholder="https://example.com/agent.jpg"
                    className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
                {agentPhotoUrl && (
                  <p className="mt-1 text-xs text-slate-500">此 URL 會用於影片結尾 Agent 頁的圓形大頭照（需為公網可達的直接圖片連結）。</p>
                )}
              </div>
            </div>

            {/* 影片版面規格（提示詞）——把口語指令整理成的「版面密碼」，可編輯後複製給 AI 溝通 */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <h2 className="flex items-center gap-2 text-xl font-bold text-slate-900">
                  <FileText className="h-5 w-5 text-yellow-600" /> 影片版面規格（提示詞）
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={copySpec}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-600 transition hover:bg-slate-50"
                  >
                    <Copy className="h-4 w-4" /> {specCopied ? '已複製 ✓' : '複製'}
                  </button>
                  <button
                    onClick={() => onSpecChange(DEFAULT_VIDEO_SPEC)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-600 transition hover:bg-slate-50"
                  >
                    <RotateCcw className="h-4 w-4" /> 重設
                  </button>
                </div>
              </div>
              <p className="text-sm text-slate-500 mb-3">
                你的歷輪指令（v4→v9 迭代）已吸收整理成這套「版面密碼」。可直接修改下方文字（自動保存在本機），
                修改後按「複製」貼給 AI，即可按新規格溝通與調整影片。
              </p>
              <textarea
                value={videoSpec}
                onChange={(e) => onSpecChange(e.target.value)}
                spellCheck={false}
                className="w-full rounded-lg border border-slate-300 bg-slate-50 p-4 font-mono text-[13px] leading-relaxed text-slate-700 outline-none focus:border-yellow-500"
                rows={20}
              />
            </div>

            {/* 影片模式交付開關——模組化預案備註（2026-08-23 定案：客人暫時只交付照片模式） */}
            <div className="bg-amber-50 rounded-xl border border-amber-200 p-6 mb-8">
              <h2 className="text-lg font-bold text-amber-900 mb-2">影片模式交付開關（預案備註）</h2>
              <ul className="list-disc list-inside space-y-1.5 text-sm text-amber-800">
                <li><strong>現狀</strong>：客人暫時只交付照片模式（照片＋FB 照片發佈＋防盜水印）。Staging 影片成果（v9 模板／影片同步 FB／IG 預覽／規格提示詞）<strong>全數保留</strong>作內部延伸開發。</li>
                <li><strong>啟用影片模式</strong>（約 10 分鐘實施）：Worker 加 <code className="bg-amber-100 px-1 rounded">GET /api/staging/features</code> 讀 KV <code className="bg-amber-100 px-1 rounded">staging_features</code>；前端依 flag 顯示 photo/video/both 選項。</li>
                <li><strong>之後一句 command 切換</strong>（<code className="bg-amber-100 px-1 rounded">apps/cms-worker</code> 目錄下、免改代碼免部署）：</li>
              </ul>
              <pre className="mt-2 rounded-lg bg-amber-900/90 p-3 text-xs leading-relaxed text-amber-50 overflow-x-auto"><code>{`# 開啟影片模式交付
npx wrangler kv key put --binding=CMS_DATA "staging_features" '{"videoPublish":true}'

# 關閉（回到純照片交付）
npx wrangler kv key put --binding=CMS_DATA "staging_features" '{"videoPublish":false}'`}</code></pre>
              <p className="mt-2 text-xs text-amber-700">詳細設計見 <code>Docs/PROMPT_MAINTAIN.md</code> §5.0。</p>
            </div>

            {/* 社群推廣設定 */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">社群推廣</h2>

              <div className="space-y-4">
                <div className="grid gap-2 md:grid-cols-2">
                  <label className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2.5 hover:bg-slate-50">
                    <input
                      type="checkbox"
                      checked={syncToFacebook}
                      onChange={(e) => setSyncToFacebook(e.target.checked)}
                      className="rounded"
                    />
                    <Facebook className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-semibold text-slate-700">Facebook 專頁<span className="ml-1 rounded bg-green-100 px-1.5 py-0.5 text-xs font-normal text-green-700">已上線</span></span>
                  </label>
                  <label className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2.5 hover:bg-slate-50">
                    <input
                      type="checkbox"
                      checked={syncToInstagram}
                      onChange={(e) => setSyncToInstagram(e.target.checked)}
                      className="rounded"
                    />
                    <Instagram className="h-4 w-4 text-purple-600" />
                    <span className="text-sm font-semibold text-slate-700">Instagram<span className="ml-1 rounded bg-purple-100 px-1.5 py-0.5 text-xs font-normal text-purple-700">預覽</span></span>
                  </label>
                </div>

                {syncToInstagram && (
                  <div className="rounded-lg border border-purple-200 bg-purple-50 p-3 text-xs text-purple-900 space-y-1">
                    <p className="font-bold">Instagram 發佈說明（尚未綁定帳號時，送出會顯示設定指引）</p>
                    <p>• 需要 IG Professional 帳號（Business／Creator）並連結現有 Facebook 專頁</p>
                    <p>• 影片：9:16 直式會以 <b>Reels</b> 發佈；1:1 方形以一般影片貼文發佈；「影片＋照片」模式下 IG 只發一支影片，不重複發輪播</p>
                    <p>• 照片模式：多張照片自動合成 <b>輪播貼文</b>（最多 10 張）</p>
                    <p>• IG 沒有「僅自己可見」：測試模式下只做媒體驗證（container 建立與轉碼檢查），不會真的發佈</p>
                    <p>
                      • Caption 上限 2,200 字：
                      {generateMessage().length > 2200 ? (
                        <span className="font-bold text-red-600">目前文案 {generateMessage().length} 字已超限，發佈前請自行刪減（系統不代刪）</span>
                      ) : (
                        `目前文案 ${generateMessage().length} 字`
                      )}
                    </p>
                  </div>
                )}

                {(syncToFacebook || syncToInstagram) && (
                  <>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">發佈方式</label>
                      <div className="space-y-2">
                        <label className="flex items-center gap-2">
                          <input
                            type="radio"
                            checked={publishMode === 'photo'}
                            onChange={() => setPublishMode('photo')}
                            className="rounded"
                          />
                          <span className="text-sm text-slate-700">只發佈照片（不使用 Quota）</span>
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="radio"
                            checked={publishMode === 'video'}
                            onChange={() => setPublishMode('video')}
                            className="rounded"
                          />
                          <span className="text-sm text-slate-700">
                            只發佈影片（使用 1 個 Quota，剩餘 {quota.total - quota.used} 個）
                          </span>
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="radio"
                            checked={publishMode === 'both'}
                            onChange={() => setPublishMode('both')}
                            className="rounded"
                          />
                          <span className="text-sm text-slate-700">
                            同時發佈影片和照片（使用 1 個 Quota，剩餘 {quota.total - quota.used} 個）
                          </span>
                        </label>
                      </div>
                      <p className="mt-2 text-xs text-slate-500">
                        提示：選擇「同時發佈影片和照片」後，系統會發佈兩個貼文：一個影片貼文（用於自然流量），一個照片貼文（用於付費廣告）。
                      </p>
                    </div>

                    {(publishMode === 'video' || publishMode === 'both') && (
                      <>
                        <div>
                          <label className="block text-sm font-semibold text-slate-700 mb-1">影片風格</label>
                          <select
                            value={videoStyle}
                            onChange={(e) => setVideoStyle(e.target.value)}
                            className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                          >
                            <option value="modern">現代簡約</option>
                            <option value="luxury">豪華風</option>
                            <option value="industrial">工業風</option>
                            <option value="cozy">溫馨家居</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-slate-700 mb-1">背景音樂</label>
                          <select
                            value={bgm}
                            onChange={(e) => setBgm(e.target.value)}
                            className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                          >
                            <option value="modern">現代簡約</option>
                            <option value="luxury">豪華風</option>
                            <option value="industrial">工業風</option>
                            <option value="cozy">溫馨家居</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-slate-700 mb-1">影片引擎</label>
                          <select
                            value={videoEngine}
                            onChange={(e) => setVideoEngine(e.target.value as 'shotstack' | 'ice' | 'zhipu' | 'aipipe')}
                            className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                          >
                            <option value="shotstack">Shotstack（模板剪輯，現行已驗證）</option>
                            <option value="ice">阿里雲 ICE（模板剪輯，PoC 對比）</option>
                            <option value="zhipu">智譜 CogVideoX（AI 文生影片，免費試效）</option>
                            <option value="aipipe">AI 全製作管線（CogVideoX 實景＋qwen-image 字卡＋ICE 合成，全阿里雲）</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-slate-700 mb-1">影片比例</label>
                          <select
                            value={aspectRatio}
                            onChange={(e) => setAspectRatio(e.target.value as 'square' | 'portrait')}
                            className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                          >
                            <option value="square">1:1 方形（1080×1080）</option>
                            <option value="portrait">9:16 直式（1080×1920，Reels 適用）</option>
                          </select>
                        </div>

                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={includeAgentPhoto}
                            onChange={(e) => setIncludeAgentPhoto(e.target.checked)}
                            className="rounded"
                          />
                          <span className="text-sm font-semibold text-slate-700">加入 Agent 照片</span>
                        </label>
                      </>
                    )}

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">
                        自訂 Hashtag（用於社群貼文，不會顯示在網站）
                      </label>
                      <input
                        type="text"
                        value={customHashtags}
                        onChange={(e) => setCustomHashtags(e.target.value)}
                        className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                        placeholder="#荀盤 #新淨 #即租即用"
                      />
                      <p className="mt-1 text-xs text-slate-500">
                        預設 Hashtag：#Marveland #金晉物業 #荃灣寫字樓
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">自訂文案（自動完整抽取物業內容，可自行刪減後發佈）</label>
                      <textarea
                        value={customMessage || defaultMessage()}
                        onChange={(e) => setCustomMessage(e.target.value)}
                        className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                        rows={4}
                      />
                      <p className="mt-1 text-xs text-slate-500">
                        系統會自動帶入物業資料，您可以在此基礎上編輯。
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* 測試模式開關 */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-slate-900">測試模式</h3>
                  <p className="text-sm text-slate-600">Facebook 貼文「僅自己可見」；Instagram 沒有此選項，測試模式下只驗證媒體、不會發佈</p>
                </div>
                <button
                  onClick={() => setTestMode(!testMode)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    testMode ? 'bg-green-600' : 'bg-slate-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      testMode ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <p className="mt-2 text-sm text-slate-500">
                目前狀態：{testMode ? <span className="font-bold text-green-600">已開啟（僅自己可見）</span> : <span className="font-bold text-red-600">已關閉（公開發佈）</span>}
              </p>
            </div>

            {/* 操作按鈕 */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
              <div className="flex gap-3">
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1 rounded-lg bg-blue-800 px-4 py-3 text-sm font-bold text-white hover:bg-blue-900 disabled:opacity-50"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      處理中...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Facebook className="h-4 w-4" />
                      {syncToInstagram && <Instagram className="h-4 w-4" />}
                      儲存並同步到{syncToFacebook && syncToInstagram ? ' Facebook 與 Instagram' : syncToInstagram ? ' Instagram' : ' Facebook'}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {/* 測試結果 */}
            {result && (
              <div className={`rounded-xl shadow-sm p-6 ${result.success ? 'bg-green-50' : 'bg-red-50'}`}>
                <div className="flex items-center gap-2 mb-4">
                  {result.success ? (
                    result.status === 'generating' ? (
                      <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
                    ) : (
                      <CheckCircle className="h-6 w-6 text-green-600" />
                    )
                  ) : (
                    <XCircle className="h-6 w-6 text-red-600" />
                  )}
                  <h3 className={`text-lg font-bold ${result.success ? (result.status === 'generating' ? 'text-blue-800' : 'text-green-800') : 'text-red-800'}`}>
                    {result.success
                      ? result.status === 'generating'
                        ? '影片生成中，請稍候...'
                        : result.status === 'pendingApproval'
                          ? '影片已生成，請審核'
                          : '發佈成功'
                      : '操作失敗'}
                  </h3>
                </div>
                <p className="text-sm text-slate-600 mb-4">{result.message}</p>

                {result.platformResults && result.platformResults.length > 0 && (
                  <div className="mb-4 space-y-2">
                    <p className="text-sm font-semibold text-slate-700">各平台結果：</p>
                    {result.platformResults.map((p, i) => (
                      <div key={i} className="flex items-start gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
                        {p.success ? (
                          <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                        ) : (
                          <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                        )}
                        <div className="text-sm">
                          <span className="font-bold text-slate-800">{p.platform}</span>
                          <span className="text-slate-600">：{p.message}</span>
                          {p.postId && <span className="ml-1 text-slate-400">（ID: {p.postId}）</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {result.videoUrl && (
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-slate-700 mb-2">影片預覽：</p>
                    <div className="rounded-lg overflow-hidden bg-black mb-3">
                      <video
                        src={result.videoUrl}
                        controls
                        className="w-full max-h-96"
                      >
                        您的瀏覽器不支援影片播放。
                      </video>
                    </div>
                    <div className="flex gap-2">
                      <a
                        href={result.videoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 rounded-lg bg-blue-100 px-3 py-2 text-sm text-blue-700 hover:bg-blue-200"
                      >
                        <Eye className="h-4 w-4" />
                        在新視窗開啟
                      </a>
                      <a
                        href={result.videoUrl}
                        download
                        className="flex items-center gap-1 rounded-lg bg-green-100 px-3 py-2 text-sm text-green-700 hover:bg-green-200"
                      >
                        <Download className="h-4 w-4" />
                        下載影片
                      </a>
                    </div>
                  </div>
                )}

                {result.shortLink && (
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-slate-700 mb-2">短網址：</p>
                    <a
                      href={result.shortLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline"
                    >
                      {result.shortLink}
                    </a>
                  </div>
                )}

                {result.postId && (
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-slate-700 mb-2">Post ID：</p>
                    <p className="text-sm text-slate-600">{result.postId}</p>
                  </div>
                )}

                {result.status === 'pendingApproval' && (
                  <div className="mt-4 flex gap-3">
                    <button
                      onClick={handleApprove}
                      disabled={loading}
                      className="flex-1 rounded-lg bg-green-600 px-4 py-3 text-sm font-bold text-white hover:bg-green-700 disabled:opacity-50"
                    >
                      {loading ? (
                        <span className="flex items-center justify-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          發佈中...
                        </span>
                      ) : (
                        `確認發佈${syncToFacebook && syncToInstagram ? '到 Facebook 與 Instagram' : syncToInstagram ? '到 Instagram' : '到 Facebook'}`
                      )}
                    </button>
                    <button
                      onClick={() => setResult(null)}
                      className="rounded-lg border border-slate-300 px-4 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50"
                    >
                      取消
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Quota 使用情況 */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-4">Quota 使用情況</h3>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-600">本月已使用</span>
                    <span className="font-bold text-slate-900">{quota.used} / {quota.total}</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${(quota.used / quota.total) * 100}%` }}
                    />
                  </div>
                </div>
                <button className="rounded-lg bg-gold-600 px-4 py-2 text-sm font-bold text-white hover:bg-gold-700">
                  購買更多 Quota
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Staging;
